import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';

import { AdminInviteStore } from './admin-invite-store';
import { ClubAdmin } from './club-admin';
import { DataInit } from './data-init';
import { MEMBER_HOME } from './landing';

/**
 * The notice, on the way into the app - the FYI page's gate.
 *
 * Sits AFTER `onboardedGuard` in the /tabs chain, and that order is load
 * bearing twice over: the server refuses an apply from an account with no
 * name (`requireOnboarded`), and the auto-join has to have run so applying
 * is an upgrade of an existing user rather than a fresh mint. Before
 * `notificationsGuard`, because who you are in the club comes before what
 * the app may send you.
 *
 * It diverts only while there is something to see: a pending invitation
 * for THIS club, not yet OK'd this session. A failed lookup surfaces as
 * no invitation (the store swallows it) - a notice nobody can fetch must
 * never hold somebody out of their club.
 */
export const adminInviteGuard: CanActivateFn & CanActivateChildFn = async () => {
  const invites = inject(AdminInviteStore);
  const router = inject(Router);

  // Data initialisation covers the invitation listener (data-init.ts) -
  // await the batch, then read.
  await inject(DataInit).whenReady();
  return invites.pending() ? router.parseUrl('/admin-invite') : true;
};

/**
 * The admin area's gate: the `adminOf` claim for this club.
 *
 * A convenience, not a boundary - the claim is a cache of the access doc,
 * and Firestore rules are what actually refuse a non-admin. Somebody who
 * lands here without it goes to the member app rather than a refusal
 * screen: they are a perfectly ordinary user of the club.
 */
export const clubAdminGuard: CanActivateFn & CanActivateChildFn = async () => {
  const clubAdmin = inject(ClubAdmin);
  const router = inject(Router);
  // Data initialisation covers the claim read (data-init.ts) - on a cold
  // load into /admin nothing has run yet, and guessing "no" would bounce
  // the club's own admin.
  await inject(DataInit).whenReady();
  return clubAdmin.isAdmin() ? true : router.parseUrl(MEMBER_HOME);
};
